import React, { useState, useEffect, useRef } from 'react'
import Axios from 'axios'
import { Link } from "react-router-dom"
import "./ShippingData.css";
import "./PoDetailModal.css";
import { Host } from './App';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// ---- Field config for the View/Edit popup ----
// Always shown
const BASE_FIELDS = [
  { key: 'PO_SHIP_DATE', label: 'PO Ship Date', type: 'date' },
  { key: 'PART_DESCRIPTION', label: 'Part Description', type: 'text' },
  { key: 'QTY', label: 'Qty', type: 'text' },
  { key: 'UNIT_PRICE', label: 'Unit Price', type: 'text' },
  { key: 'COMMENTS', label: 'Comments', type: 'textarea' },
  { key: 'SALES_ORDER_NO', label: 'Sales Order No', type: 'text' },
];

// Only shown when the part's UPHOLSTERY flag (from PART_DATASET) is YES
const UPHOLSTERY_FIELDS = [
  { key: 'FABRIC_STATUS', label: 'Fabric Status', type: 'text' },
  { key: 'FABRIC_STYLE', label: 'Fabric Style', type: 'text' },
  { key: 'FABRIC_COLOR', label: 'Fabric Color', type: 'text' },
  { key: 'ORIENTATION', label: 'Orientation', type: 'text' },
  { key: 'BASE_COLOR', label: 'Base Color', type: 'text' },
  { key: 'FABRIC_INSIDE_BACK', label: 'Fabric Inside Back', type: 'text' },
  { key: 'FABRIC_OUTSIDE_BACK', label: 'Fabric Outside Back', type: 'text' },
  { key: 'FABRIC_SEAT', label: 'Fabric Seat', type: 'text' },
  { key: 'PIPING', label: 'Piping', type: 'text' },
  { key: 'FABRIC_BACK', label: 'Fabric Back', type: 'text' },
  { key: 'WOOD_OUTSIDE_BACK', label: 'Wood Outside Back', type: 'text' },
  { key: 'MOLDING', label: 'Molding', type: 'text' },
  { key: 'WOODSHOP', label: 'Woodshop', type: 'text' },
  { key: 'UPHOLSTERY', label: 'Upholstery', type: 'text' },
  { key: 'Wood_Seat_And_Back_Species', label: 'Wood Seat/Back Species', type: 'text' },
  { key: 'Wood_Seat_And_Back_Stain_Color', label: 'Wood Seat/Back Stain Color', type: 'text' },
  { key: 'Seat_Pad_Fabric', label: 'Seat Pad Fabric', type: 'text' },
  { key: 'Wood_Ring', label: 'Wood Ring', type: 'text' },
];

function isUpholsteryPart(row) {
  const flag = row.PD_UPHOLSTERY ?? row.UPHOLSTERY;
  return !!flag && flag.toString().trim().toUpperCase() === 'YES';
}

// ---- View / Edit modal (one row at a time) ----
function ViewEditModal({ row, token, onClose, onSaved }) {
  const fields = isUpholsteryPart(row) ? [...BASE_FIELDS, ...UPHOLSTERY_FIELDS] : BASE_FIELDS;
  const initial = {};
  fields.forEach(f => {
    let v = row[f.key];
    if (f.type === 'date' && v) v = v.slice(0, 10);
    initial[f.key] = v ?? '';
  });

  const [formData, setFormData] = useState(initial);
  const [saving, setSaving] = useState(false);

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    setSaving(true);
    Axios.post(`https://` + Host + `/api/po/UpdateDetail`, {
      SR_NO: row.SR_NO,
      fields: formData,
      token: token,
    }).then((response) => {
      setSaving(false);
      if (response.status === 200) {
        toast.success(response.data.message || "Updated successfully", {
          autoClose: 1500,
          position: toast.POSITION.TOP_RIGHT,
        });
        onSaved({ ...row, ...formData });
        onClose();
      }
    }).catch((error) => {
      setSaving(false);
      toast.error(
        error?.response?.data?.message || "Error updating the row",
        { autoClose: 3000, position: toast.POSITION.TOP_RIGHT }
      );
    });
  };

  const upholstery = isUpholsteryPart(row);
  const baseFieldSet = fields.filter(f => BASE_FIELDS.some(bf => bf.key === f.key));
  const upholsteryFieldSet = fields.filter(f => UPHOLSTERY_FIELDS.some(uf => uf.key === f.key));

  const renderField = (f) => (
    <div
      className={
        "po-detail-modal-field" + (f.type === 'textarea' ? " po-detail-modal-field-wide" : "")
      }
      key={f.key}
    >
      <label>{f.label}</label>
      {f.type === 'textarea' ? (
        <textarea
          rows={3}
          value={formData[f.key]}
          onChange={(e) => handleChange(f.key, e.target.value)}
        />
      ) : (
        <input
          type={f.type}
          value={formData[f.key]}
          onChange={(e) => handleChange(f.key, e.target.value)}
        />
      )}
    </div>
  );

  return (
    <div className="po-detail-modal-overlay" onClick={onClose}>
      <div className="po-detail-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="po-detail-modal-header">
          <h4 className="po-detail-modal-title">
            PO {row.PO_NO} <small>/ Part {row.PART_NO}</small>
            {upholstery ? <span className="po-detail-modal-badge">Upholstery</span> : null}
          </h4>
          <button
            type="button"
            className="po-detail-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="po-detail-modal-body">
          <div className="po-detail-modal-section-label">General</div>
          <div className="po-detail-modal-grid">
            {baseFieldSet.map(renderField)}
          </div>

          {upholstery && upholsteryFieldSet.length > 0 && (
            <>
              <div className="po-detail-modal-section-label">Upholstery Details</div>
              <div className="po-detail-modal-grid">
                {upholsteryFieldSet.map(renderField)}
              </div>
            </>
          )}
        </div>

        <div className="po-detail-modal-footer">
          <button
            className="po-detail-modal-btn po-detail-modal-btn-cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="po-detail-modal-btn po-detail-modal-btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UpdatePOData() {
  const debounceTimeout = useRef(null);

  const nowDate = new Date();
  const [editingStatus, setEditingStatus] = useState({});

  var int = 4;
  var [postList, setPostList] = useState([]);
  var [PoNo, setPoNo] = useState({});
  var [PartNo, setPartNo] = useState({});
  var [Date1, setDate1] = useState(new Date());
  var [Postartdate, setPoStartdate] = useState('2026-01-01');
  var [Poenddate, setPoEnddate] = useState(nowDate.toISOString().slice(0, 10));
  var [customer, setCustomer] = useState({});
  var [NewPoQty, setNewPoQty] = useState("");
  const [del, setdel] = useState(0);
  const [openorder, setopenorder] = useState("1");
  var [Customers, setCustomers] = useState([]);
  var [partNo, setpartNo] = useState([]);
  var [order, setOrder] = useState("1");
  var [test, setTest] = useState(int);
  var token = (window.localStorage.getItem("acessToken"));
  const [isLoading, setIsLoading] = useState(false);
  const [inputValues, setInputValues] = useState({});

  // Status columns
  const [statusColumnsCount, setStatusColumnsCount] = useState({});
  const [statusData, setStatusData] = useState({});

  // NEW: which row (by index) has its View/Edit modal open
  const [viewRowIndex, setViewRowIndex] = useState(null);

  useEffect(() => {
    var timer = setInterval(() => setDate1(new Date()), 10)
    return function cleanup() {
      clearInterval(timer)
    }
  });

  // Default Data
  useEffect(() => {
    Axios.get(`https://` + Host + `/api/Shipping/searchupdatepo/${JSON.stringify(PoNo)},${JSON.stringify(PartNo)},${Postartdate},${Poenddate},${JSON.stringify(customer)},${order},${token}`).then((data) => {
      const fetchedData = data.data;
      setPostList(fetchedData);

      const initialStatusColumnsCount = {};
      const initialStatusData = {};

      fetchedData.forEach((row, index) => {
        if (row.combinedStatuses) {
          const statuses = row.combinedStatuses.split(" || ").filter(s => s.trim() !== "");
          initialStatusColumnsCount[index] = statuses.length || 0;
          initialStatusData[index] = statuses;
        } else {
          initialStatusColumnsCount[index] = 0;
          initialStatusData[index] = [];
        }
      });

      setStatusColumnsCount(initialStatusColumnsCount);
      setStatusData(initialStatusData);
    })
  }, [PoNo, PartNo, Postartdate, Poenddate, customer, order, NewPoQty, del]);

  useEffect(() => {
    Axios.get(`https://` + Host + `/api/get/Customers/${token}`).then((data) => {
      setCustomers(data.data)
    });
  }, [])

  useEffect(() => {
    Axios.get(`https://` + Host + `/api/get/partNo/${token}`).then((data) => {
      setpartNo(data.data)
    });
  }, [])

  const [changedLine, setChangedLine] = useState(new Set());
  const handleInputChange = (e, index, key) => {
    const { value } = e.target;
    updateDatas(index, key, value);
  };

  const updateDatas = (index, key, value) => {
    setPostList(prevData => {
      const newData = [...prevData];
      newData[index][key] = value;
      return newData;
    });
    setChangedLine(prevRows => new Set(prevRows).add(index));
  };

  const addStatusColumn = (rowIndex) => {
    if ((statusColumnsCount[rowIndex] || 0) < 10) {
      setStatusColumnsCount(prev => ({
        ...prev,
        [rowIndex]: (prev[rowIndex] || 0) + 1
      }));
      setStatusData(prev => {
        const newData = { ...prev };
        if (!newData[rowIndex]) {
          newData[rowIndex] = [];
        }
        return newData;
      });
    } else {
      toast.error("Maximum 10 status columns allowed", {
        autoClose: 3000,
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  const handleStatusChange = (e, rowIndex, statusIndex) => {
    const userInput = e.target.value.trim();

    setStatusData(prev => {
      const newData = { ...prev };
      if (!newData[rowIndex]) {
        newData[rowIndex] = [];
      }
      newData[rowIndex][statusIndex] = userInput;
      return newData;
    });

    setChangedLine(prevRows => new Set(prevRows).add(rowIndex));
    updatePostListStatus(rowIndex);
  };

  const buildCombinedStatus = (rowIndex) => {
    if (!statusData[rowIndex]) return undefined;
    return statusData[rowIndex]
      .slice(0, statusColumnsCount[rowIndex] || 0)
      .filter(status => status && status.trim() !== "")
      .map(status => {
        if (status && !status.includes(':')) {
          const day = String(Date1.getDate()).padStart(2, '0');
          const month = String(Date1.getMonth() + 1).padStart(2, '0');
          const year = Date1.getFullYear();
          const formattedDate = `${day}-${month}-${year}`;
          return `${status}:${formattedDate}`;
        }
        return status;
      })
      .join(" || ");
  };

  const updatePostListStatus = (rowIndex) => {
    setPostList(prevData => {
      const newData = [...prevData];
      const combined = buildCombinedStatus(rowIndex);
      if (combined !== undefined) {
        newData[rowIndex].STATUS = combined;
      }
      return newData;
    });
  };

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      const changedData_ = [...changedLine].map(index => {
        const row = postList[index];
        const combined = buildCombinedStatus(index);
        if (combined !== undefined) {
          row.STATUS = combined;
        }
        return row;
      });

      if (changedData_.length > 0) {
        setIsLoading(true);
        Axios.post(`https://` + Host + `/api/po/Update`, {
          changedData: JSON.stringify(changedData_),
          token: token
        }).then((response) => {
          if (response.status === 200) {
            toast.success(response.data.message, {
              autoClose: 1000,
              position: toast.POSITION.TOP_RIGHT,
              style: { fontSize: '24px', borderRadius: '10px', color: 'red' },
            });
            setChangedLine(new Set());
            setTimeout(() => { setdel(del + 1); }, 2000);
            setIsLoading(false);
          }
        })
          .catch(function (error) {
            window.alert(error);
            setIsLoading(false);
          });
      } else {
        toast.error("No Valid Data !", {
          autoClose: 3000,
          position: toast.POSITION.TOP_RIGHT,
          style: { fontSize: '24px', borderRadius: '10px' },
        });
      }
    }, 500);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [changedLine]);

  const Update = (PO_NO, PART_NO, Po_Ship_Date, QTY, comments) => {
    const changedData_ = [...changedLine].map(index => {
      const row = postList[index];
      const combined = buildCombinedStatus(index);
      if (combined !== undefined) {
        row.STATUS = combined;
      }
      return row;
    });

    Axios.post(`https://` + Host + `/api/po/Update`, { changedData: JSON.stringify(changedData_), token: token }).then((response) => {
      if (response.status === 200) {
        window.alert(response.data.message);
        setdel(del + 1);
        window.location.reload();
      }
    })
      .catch(function (error) {
        console.log(error.response);
      });
  };

  const Delete = (PO_NO, PART_NO) => {
    Axios.delete(`https://` + Host + `/api/Shippeddata/Delete/${PO_NO},${PART_NO},${token}`).then((response) => {
      if (response.status === 200) {
        toast.success(response.data.message, {
          autoClose: 1000,
          position: toast.POSITION.TOP_RIGHT,
          style: { fontSize: '24px', borderRadius: '10px', color: 'red' },
        });
        setdel(del + 1);
      }
    })
      .catch(function (error) {
        console.log(error.response);
      });
  };

  // NEW: called when the modal successfully saves — patch local row so table reflects it without a full refetch
  const handleRowSaved = (rowIndex, updatedRow) => {
    setPostList(prev => {
      const newData = [...prev];
      newData[rowIndex] = { ...newData[rowIndex], ...updatedRow };
      return newData;
    });
  };

  return (
    <div className="MainPage_data">
      <div className="Container">
        <ToastContainer />
        <div className="row">
          <div className="col">
            <input type='text' className="form-control form-rounded" placeholder='PO NO' list="operators" onChange={(e) => {
              setPoNo(e.target.value)
            }} />
          </div>
          <div className="col">
            <input type='text' className="form-control form-rounded" placeholder='PART NO' list="PartNo" onChange={(e) => {
              setPartNo(e.target.value)
            }} />
            <datalist id="PartNo">
              <option></option>
              {partNo.map(x => {
                return <option key={x.Part_No}>{x.Part_No}</option>
              })}
            </datalist>
          </div>
          <div className="col">
            <div className="row">
              <div className="col" id='moldng'>
                <input type='date' className="form-control form-rounded" placeholder='PO SHIP DATE' defaultValue={Postartdate} onChange={(e) => {
                  setPoStartdate(e.target.value)
                }} />
                <span className="tooltiptext">Filter By PO SHIP DATE </span>
              </div>
              <div className="col">
                <input type='date' className="form-control form-rounded" placeholder='Operator' defaultValue={Date1.toISOString().slice(0, 10)} onChange={(e) => {
                  setPoEnddate(e.target.value)
                }} />
              </div>
            </div>
          </div>
          <div className="col">
            <div className="row">
              <input type='text' className="form-control form-rounded" placeholder='Customer' list='Customers' onChange={(e) => {
                setCustomer(e.target.value)
              }} />
              <datalist id="Customers">
                <option></option>
                {Customers.map(x => {
                  return <option key={x.Customer}>{x.Customer}</option>
                })}
              </datalist>
            </div>
            <div className="row">
              <select
                className="form-control form-rounded"
                id="order"
                name="order"
                onChange={(e) => {
                  setOrder(e.target.value);
                  order = e.target.value;
                }}
              >
                <option value="1">Open Orders</option>
                <option value="0"> Open/Closed Orders</option>
              </select>
            </div>
          </div>
          <div className="col">
            <div className="row">
              <Link to="/shippedparts">
                <input
                  type="button"
                  className="btn btn-success"
                  defaultValue="Review Shipped Data"
                  name="Section"
                  id="btn_type"
                />
              </Link>
            </div>
            <div className="row">
              <Link to="/addpartdataset">
                <input
                  type="button"
                  className="btn btn-success"
                  defaultValue="Add new Part Dataset"
                  name="Section"
                  id="btn_type"
                />
              </Link>
            </div>
          </div>
        </div>
        <br></br>
        <br></br>
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>PO NO</th>
              <th>PART NO</th>
              <th>PO SHIP DATE</th>
              <th>PART DESCRIPTION</th>
              <th>CUSTOMER</th>
              <th>PO QTY</th>
              <th>REMAINED QTY</th>
              <th>ORDER DATE</th>
              <th>UNIT PRICE</th>
              <th id="comment">COMMENTS</th>
              <th colSpan="2">STATUS</th>
              <th>Details</th>
              <th>Update</th>
              <th>Delete</th>
            </tr>
          </thead>
          {postList.map((val, index) => {
            const rowStatusCount = statusColumnsCount[index] || 0;
            const rowStatusData = statusData[index] || [];

            return (
              <tbody key={val.SR_NO ?? index}>
                <tr>
                  <td>{val.PO_NO}</td>
                  <td>{val.PART_NO}</td>
                  <td>
                    <input
                      type="date"
                      className="form-control form-rounded"
                      value={(val.PO_SHIP_DATE || '').slice(0, 10)}
                      onChange={(e) => handleInputChange(e, index, 'PO_SHIP_DATE')}
                    />
                  </td>
                  <td>{val.PART_DESCRIPTION}</td>
                  <td>{val.CUSTOMER}</td>
                  <td id={val.SR_NO}>
                    <input
                      type="text"
                      name='qty'
                      className="form-control form-rounded"
                      value={val.QTY}
                      onChange={(e) => handleInputChange(e, index, 'QTY')}
                    />
                  </td>
                  <td>{val.Remained_parts}</td>
                  <td>{(val.PO_DATE || '').slice(0, 10)}</td>
                  <td id={val.SR_NO}>
                    <input
                      type="text"
                      name='Unit_Price'
                      className="form-control form-rounded"
                      value={val.UNIT_PRICE}
                      onChange={(e) => handleInputChange(e, index, 'UNIT_PRICE')}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-rounded"
                      value={inputValues[index] !== undefined ? inputValues[index] : val.COMMENTS || ''}
                      onChange={(e) =>
                        setInputValues(prev => ({ ...prev, [index]: e.target.value }))
                      }
                      onBlur={(e) => {
                        handleInputChange(e, index, 'COMMENTS');
                        setInputValues(prev => {
                          const newVals = { ...prev };
                          delete newVals[index];
                          return newVals;
                        });
                      }}
                    />
                  </td>

                  <td colSpan="2">
                    {Array.from({ length: rowStatusCount }).map((_, statusIndex) => {
                      const currentStatus = rowStatusData[statusIndex] || "";
                      return (
                        <div key={`status-${index}-${statusIndex}`} style={{ marginBottom: '5px' }}>
                          <div className="input-group">
                            <textarea
                              className="form-control form-rounded"
                              style={{
                                minWidth: '45vh',
                                minHeight: '80px',
                                resize: 'vertical'
                              }}
                              value={
                                editingStatus[`${index}-${statusIndex}`] ??
                                currentStatus ??
                                ""
                              }
                              onChange={(e) =>
                                setEditingStatus(prev => ({
                                  ...prev,
                                  [`${index}-${statusIndex}`]: e.target.value
                                }))
                              }
                              onBlur={(e) => {
                                handleStatusChange(e, index, statusIndex);
                                setEditingStatus(prev => {
                                  const updated = { ...prev };
                                  delete updated[`${index}-${statusIndex}`];
                                  return updated;
                                });
                              }}
                              placeholder={`Status ${statusIndex + 1}`}
                              rows={3}
                            />
                          </div>
                        </div>
                      );
                    })}

                    <button
                      type="button"
                      className="btn btn-info btn-sm mt-2"
                      onClick={() => addStatusColumn(index)}
                      disabled={rowStatusCount >= 10}
                    >
                      + Add Status {rowStatusCount > 0 ? `(${rowStatusCount}/10)` : ''}
                    </button>
                  </td>

                  {/* NEW: Details / View button */}
                  <td>
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => setViewRowIndex(index)}
                    >
                      View
                    </button>
                  </td>

                  <td>
                    <button
                      className="btn btn-success"
                      onClick={() => {
                        Update((val.PO_NO), (val.PART_NO), (val.PO_SHIP_DATE || '').slice(0, 10), (val.QTY), (val.COMMENTS))
                      }}
                    >
                      Update
                    </button>
                  </td>
                  <td>
                    <button
                      className="btn btn-danger"
                      onClick={() => {
                        window.confirm('Are you sure you want to delete this Entry?') && Delete((val.PO_NO), (val.PART_NO))
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              </tbody>
            )
          })}
        </table>
      </div>

      {/* NEW: View/Edit popup, one row at a time */}
      {viewRowIndex !== null && postList[viewRowIndex] && (
        <ViewEditModal
          row={postList[viewRowIndex]}
          token={token}
          onClose={() => setViewRowIndex(null)}
          onSaved={(updatedRow) => handleRowSaved(viewRowIndex, updatedRow)}
        />
      )}

      {isLoading ?
        <div className='overlay'>
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div> : null}
    </div>
  )
}

export default UpdatePOData